import { darkTheme } from "@nudgebox/ui";
import * as stylex from "@stylexjs/stylex";
import { useSyncExternalStore } from "react";

/** What the user picked. `system` follows the OS appearance setting. */
export type ThemePreference = "light" | "dark" | "system";
const STORAGE_KEY = "nudgebox:theme";
const listeners = new Set<() => void>();
const darkClasses = (stylex.props(...darkTheme).className ?? "").split(" ").filter(Boolean);
const systemDark = () => window.matchMedia("(prefers-color-scheme: dark)");
function isPreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : "system";
  } catch {
    // Storage can be blocked (private mode, disabled site data).
    return "system";
  }
}
let preference: ThemePreference = "system";
function apply() {
  const dark = preference === "dark" || preference === "system" && systemDark().matches;
  const root = document.documentElement;
  // Theme overrides live on <html> so portaled UI (dialogs, menus, toasts) inherits them.
  for (const className of darkClasses) root.classList.toggle(className, dark);
  root.style.colorScheme = dark ? "dark" : "light";
  // Tints the mobile browser / standalone status bar to match the page.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#0a0b0d" : "#f6f7f9");
}
function setAndNotify(next: ThemePreference) {
  preference = next;
  apply();
  for (const listener of listeners) listener();
}

/** Applies the saved theme and keeps it in sync with the OS and other tabs. Call once before rendering. */
export function initTheme() {
  preference = readPreference();
  apply();
  systemDark().addEventListener("change", apply);
  window.addEventListener("storage", event => {
    if (event.key === STORAGE_KEY) setAndNotify(readPreference());
  });
}
export function setThemePreference(next: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Still apply for this session.
  }
  setAndNotify(next);
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => preference);
}